/**
 * English: Read-only overview of device agents per station with links to Chat Test and Device Control.
 * Thai: ภาพรวม device agents ต่อสถานี (อ่านอย่างเดียว) พร้อมลิงก์ไป Chat Test และ Device Control
 */
import { useTranslation } from 'react-i18next';
import { ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import PageShell from '../components/layout/PageShell';
import PageHeader from '../components/layout/PageHeader';
import AgentManagementGrid from '../components/agent-management/AgentManagementGrid';
import StationManagerSection from '../components/agent-management/StationManagerSection';
import AgentManagementOrgBar from '../components/agent-management/AgentManagementOrgBar';
import AgentManagementStationList from '../components/agent-management/AgentManagementStationList';
import { useAgentRows } from '../components/agent-management/useAgentRows';
import { useAgentManagementScope } from '../components/agent-management/useAgentManagementScope';

export default function AgentManagementPage() {
  const { t } = useTranslation();
  const scope = useAgentManagementScope();
  const { rows, loading, awaitingStation, refresh, deviceTypes } = useAgentRows(scope.stationId);

  const handleRefresh = () => {
    void refresh();
    void scope.refreshStations();
    void scope.refreshOrgs();
  };

  return (
    <PageShell>
      <div className="mb-4">
        <PageHeader title={t('agents.title')} description={t('agents.description')}>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading && rows.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-app-border bg-app-surface text-sm font-medium text-app-text hover:bg-app-muted disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </button>
        </PageHeader>
      </div>

      <div className="mb-6 flex gap-3 rounded-xl border border-app-border/60 bg-app-accent/5 px-4 py-3 text-sm text-app-text-sec">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-app-accent" />
        <div className="space-y-1">
          <p>{t('agents.infoCache')}</p>
          <p className="text-xs text-app-text-dim">{t('agents.infoKpi')}</p>
        </div>
      </div>

      <div className="space-y-6">
        <AgentManagementOrgBar
          orgName={scope.orgName}
          enabledOrgs={scope.enabledOrgs}
          orgsLoading={scope.orgsLoading}
          onOrgChange={scope.setOrgName}
        />

        {scope.orgName ? (
          <AgentManagementStationList
            orgName={scope.orgName}
            stations={scope.orgStations}
            stationId={scope.stationId}
            loading={scope.stationsLoading}
            onStationSelect={scope.setStationId}
          />
        ) : null}

        {scope.orgName && scope.stationId ? (
          <div className="space-y-6">
            <StationManagerSection
              orgName={scope.orgName}
              stationId={scope.stationId}
              stations={scope.orgStations}
              onStationChange={scope.setStationId}
              stationsLoading={scope.stationsLoading}
            />
            <AgentManagementGrid
              rows={rows}
              loading={loading}
              awaitingStation={awaitingStation}
              deviceTypes={deviceTypes}
              orgName={scope.orgName}
              orgId={scope.orgId}
              stationId={scope.stationId}
            />
          </div>
        ) : null}
      </div>
    </PageShell>
  );
}
